/**
 * Протокол демона подписи. §Ключ 2.2, §Ключ 2.4.
 *
 * В демон уезжает ПОЛНЫЙ канонический контент, а не хэш. Вариант из 04-backend-http.md §12
 * (`{version_id, content_hash, slot_at, nonce, …}`) отвергнут: с ним демон не может выполнить
 * ни одной своей проверки — ни cv, ни переканонизацию, ни лимиты Telegram, — то есть исчезает
 * вся гарантия «пост, на который Telegram ответил бы 400, не может получить ключ».
 *
 * Бэкенд НЕ выбирает nonce и НЕ собирает payload: и то и другое делает демон.
 * Поля totp в схеме v1 нет — решение Ильи 22.08 про step-up.
 */

import * as z from 'zod';
import {
  APPROVER_RE,
  BRAND_RE,
  CHAT_ID_RE,
  CONTENT_HASH_RE,
  KID_RE,
  NONCE_RE,
} from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { DraftContentSchema } from '../telegram/draft.ts';

const Uuid = z.string().regex(UUID_V7_RE);

/** Allowlist полей: любое лишнее → 400 BAD_REQUEST_SHAPE. */
export const SignRequestSchema = z
  .strictObject({
    request_id: Uuid,
    brand: z.string().regex(BRAND_RE),
    draft_id: Uuid,
    draft_version: z.int().positive(),
    publication_id: Uuid,
    /** `preview` — отправка в тестовый канал (§9.1): слот «сейчас», без свежести, без message_id. */
    action: z.enum(['publish', 'revoke', 'preview']),
    target: z.strictObject({
      adapter: z.literal('telegram'),
      chat_id: z.string().regex(CHAT_ID_RE),
    }),
    content: DraftContentSchema,
    slot: z.strictObject({ start_ts: z.int(), deadline_ts: z.int() }),
    freshness: z.strictObject({ published_at_ts: z.int(), max_age_s: z.int() }).nullable(),
    approver: z.string().regex(APPROVER_RE),
    message_id: z.int().positive().nullable(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if ((v.action === 'revoke') !== (v.message_id !== null)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['message_id'],
        message: 'action_message_id_mismatch',
      });
    }
    if (v.action === 'preview' && v.freshness !== null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['freshness'],
        message: 'preview_freshness_forbidden',
      });
    }
  });
export type SignRequest = z.output<typeof SignRequestSchema>;

export const SignResponseSchema = z.strictObject({
  key: z.string().max(8192),
  kid: z.string().regex(KID_RE),
  key_seq: z.int().positive(),
  nonce: z.string().regex(NONCE_RE),
  content_hash: z.string().regex(CONTENT_HASH_RE),
  canon_algo_version: z.int(),
  issued_at_ts: z.int(),
  slot: z.strictObject({ start_ts: z.int(), deadline_ts: z.int() }),
  /** Единственное, чем бэкенд сверяет свою строку с журналом демона в суточной сверке. */
  payload_sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export type SignResponse = z.output<typeof SignResponseSchema>;

/**
 * Коды отказа демона, в порядке проверок §Ключ 2.3.
 *
 * Два кода 2026-10-25 (docs/ai-lab.md) стоят за проверкой адресата — это тоже вопросы
 * «можно ли этому бренду», а не «годится ли пост»:
 *   • `AUTO_APPROVAL_NOT_ALLOWED` — `approver === AUTO_APPROVER`, а бренда нет в allowlist'е
 *     автоодобрения демона (замок 2 из 3) или действие не `publish`;
 *   • `DRAFT_BRAND_MISMATCH` — этот `draft_id` демон уже подписывал под другим брендом.
 */
export const SIGN_REFUSAL_CODES = [
  'REQUEST_ID_REUSED',
  'REQUEST_ID_REUSED_AFTER_RESTART',
  'BAD_REQUEST_SHAPE',
  'TARGET_NOT_ALLOWED',
  'AUTO_APPROVAL_NOT_ALLOWED',
  'DRAFT_BRAND_MISMATCH',
  'CONTENT_SCHEMA',
  'CANON_ALGO_MISMATCH',
  'CONTENT_NOT_CANONICAL',
  'TELEGRAM_INVALID',
  'SLOT_WINDOW_INVALID',
  'SLOT_IN_PAST',
  'KEY_LIFETIME_EXCEEDED',
  'FRESHNESS_INVALID',
  'RATE_LIMITED',
  'UNSUPPORTED_MEDIA_TYPE',
  'PAYLOAD_TOO_LARGE',
  'NOT_FOUND',
] as const;
export type SignRefusalCode = (typeof SIGN_REFUSAL_CODES)[number];

export const SignErrorSchema = z.strictObject({
  error: z.strictObject({
    code: z.string(),
    message: z.string(),
    details: z.looseObject({}).optional(),
  }),
});
